// Import Dependencies
import { useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import { jwtDecode } from "jwt-decode";

// Local Imports
import { loginUser, getUserProfile, createUser, updateUserProfile, registerAdmin } from "app/services/auth";
import { isTokenValid, setSession, getCurrentUser } from "infrastructure/utils/jwt";
import { supabase } from "infrastructure/utils/supabase";
import { AuthContext } from "./context";

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  errorMessage: null,
  user: null,
};

const reducerHandlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      isLoading: false,
      user,
    };
  },

  LOGIN_REQUEST: (state) => {
    return {
      ...state,
      isLoading: true,
      errorMessage: null,
    };
  },

  LOGIN_SUCCESS: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      isLoading: false,
      errorMessage: null,
      user,
    };
  },

  REGISTER_SUCCESS: (state, action) => {
    const { message } = action.payload;
    return {
      ...state,
      isAuthenticated: false,
      isLoading: false,
      errorMessage: null,
      user: null,
      successMessage: message,
    };
  },

  LOGIN_ERROR: (state, action) => {
    const { errorMessage } = action.payload;

    return {
      ...state,
      errorMessage,
      isLoading: false,
      isAuthenticated: false,
      user: null,
    };
  },

  UPDATE_PROFILE: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      user: { ...state.user, ...user },
    };
  },

  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
    errorMessage: null,
  }),
};

const reducer = (state, action) => {
  const handler = reducerHandlers[action.type];
  if (handler) {
    return handler(state, action);
  }
  return state;
};

const resolveRole = (user) => {
  const roleCandidates = [
    user?.role,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.metadata?.role,
    user?.metadata?.rawAppMetaData?.role,
    user?.metadata?.rawUserMetaData?.role
  ]
    .filter(Boolean)
    .map(role => String(role).toLowerCase());

  if (roleCandidates.some(role => ['admin', 'superadmin', 'super_admin', 'entreprise', 'controleur', 'accountant', 'support', 'service_role'].includes(role))) {
    return 'admin';
  }

  if (roleCandidates.some(role => ['chauffeur', 'driver'].includes(role))) {
    return 'chauffeur';
  }

  return roleCandidates[0] || 'authenticated';
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        // Utiliser getActiveToken pour supporter Supabase et le stockage legacy
        const { getActiveToken } = await import("infrastructure/utils/jwt");
        const authToken = getActiveToken();

        // Nettoyer immédiatement les tokens manifestement invalides
        if (authToken) {
          try {
            const decoded = jwtDecode(authToken);
            // Vérifier si c'est un token avec une signature fake ou un ID invalide
            if (authToken.includes('fake-signature') ||
              (decoded.sub && typeof decoded.sub === 'string' && decoded.sub.startsWith('uid-')) ||
              (decoded.userId && typeof decoded.userId === 'string' && decoded.userId.startsWith('uid-'))) {
              setSession(null);
              dispatch({
                type: "INITIALIZE",
                payload: {
                  isAuthenticated: false,
                  user: null,
                },
              });
              return;
            }
          } catch {
            setSession(null);
            dispatch({
              type: "INITIALIZE",
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            return;
          }
        }

        if (authToken && isTokenValid(authToken)) {
          const userFromToken = getCurrentUser();

          // Détecter les tokens locaux par leur signature ou leur contenu
          const isLocalToken = authToken.includes('fake-signature') ||
            (userFromToken && typeof userFromToken.id === 'string' && userFromToken.id.startsWith('uid-'));

          if (isLocalToken || !userFromToken) {
            setSession(null);
            dispatch({
              type: "INITIALIZE",
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            return;
          }

          setSession(authToken);

          if (userFromToken) {

            // Récupérer le profil complet depuis la base de données
            try {
              const fullUserProfile = await getUserProfile(userFromToken.id);

              dispatch({
                type: "INITIALIZE",
                payload: {
                  isAuthenticated: true,
                  user: fullUserProfile,
                },
              });
            } catch (profileError) {
              // Si la session est expirée ou l'utilisateur n'existe plus
              if (profileError.message?.includes('Session expirée') ||
                profileError.message?.includes('Utilisateur non trouvé')) {
                setSession(null);
                dispatch({
                  type: "INITIALIZE",
                  payload: {
                    isAuthenticated: false,
                    user: null,
                  },
                });
                return;
              }

              // Pour d'autres erreurs, utiliser les données du token comme fallback
              dispatch({
                type: "INITIALIZE",
                payload: {
                  isAuthenticated: true,
                  user: userFromToken,
                },
              });
            }
          } else {
            setSession(null);
            dispatch({
              type: "INITIALIZE",
              payload: {
                isAuthenticated: false,
                user: null,
              },
            });
            return;
          }
        } else {
          // Token invalide ou expiré
          setSession(null);
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch {
        // Nettoyer en cas d'erreur
        setSession(null);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    init();
  }, []);

  // Écouter les changements de session Supabase (refresh token, déconnexion, etc.)
  useEffect(() => {
    if (!supabase) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        // Supabase a rafraîchi le token — synchroniser avec notre stockage
        setSession(session.access_token);
      } else if (event === 'SIGNED_OUT') {
        // L'utilisateur a été déconnecté (token révoqué, session expirée, etc.)
        setSession(null);
        dispatch({
          type: "LOGOUT",
        });
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = async ({ username, password }) => {
    dispatch({
      type: "LOGIN_REQUEST",
    });

    try {
      // Utiliser notre service d'authentification local
      const { user, token } = await loginUser(username, password);

      // Isolation: Vérifier si le rôle de l'utilisateur correspond au rôle attendu par l'application
      const appMode = import.meta.env.VITE_APP_MODE;
      const userRole = resolveRole(user);

      const isAdminApp = appMode === "admin" || appMode === "entreprise" || appMode === "admin-limited";
      const isAdminProfile = userRole === 'admin';
      const isDriverProfile = userRole === 'chauffeur';

      // Si on est en mode admin mais l'utilisateur n'est pas autorisé
      if (isAdminApp && !isAdminProfile) {
        throw new Error("Accès refusé: Ce compte n'a pas les droits d'administration pour cette interface.");
      }

      // Si on est en mode chauffeur mais l'utilisateur n'est pas autorisé
      if (appMode === 'driver' && !isDriverProfile) {
        throw new Error("Accès refusé: Ce compte n'est pas un compte chauffeur.");
      }

      if (!(user !== null && typeof user === 'object')) {
        throw new Error("Réponse d'authentification invalide");
      }

      setSession(token);

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: {
          user,
        },
      });

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || "Erreur de connexion";
      dispatch({
        type: "LOGIN_ERROR",
        payload: {
          errorMessage: { message: errorMessage },
        },
      });
      throw err;
    }
  };

  const register = async (userData) => {
    dispatch({
      type: "LOGIN_REQUEST",
    });

    try {
      // Déterminer quel service utiliser selon le rôle
      const isRegisterAdmin = userData.role === 'admin' || userData.role === 'entreprise';

      let newUser;
      if (isRegisterAdmin) {
        // Pour les admins, on crée le compte et la société simultanément
        newUser = await registerAdmin(userData);
      } else {
        // Pour les chauffeurs, on utilise la création standard
        newUser = await createUser(userData);
      }

      if (!(newUser !== null && typeof newUser === 'object')) {
        throw new Error("Erreur lors de la création de l'utilisateur");
      }

      // NE PAS connecter automatiquement l'utilisateur après l'inscription
      // L'utilisateur doit se connecter manuellement après la création du compte

      dispatch({
        type: "REGISTER_SUCCESS", // Nouveau type d'action pour l'inscription réussie
        payload: {
          message: "Compte créé avec succès. Veuillez vous connecter.",
        },
      });

      return { success: true, message: "Compte créé avec succès. Veuillez vous connecter." };
    } catch (err) {
      const errorMessage = err.message || "Erreur lors de l'inscription";
      dispatch({
        type: "LOGIN_ERROR",
        payload: {
          errorMessage: { message: errorMessage },
        },
      });
      throw err;
    }
  };

  const logout = async () => {
    // 1. CRITICAL: Appeler supabase.auth.signOut() pour invalider la session côté serveur
    // et nettoyer le stockage interne de Supabase (sinon la session est restaurée au rechargement)
    try {
      const { supabase: supabaseClient } = await import('infrastructure/utils/supabase.js');
      if (supabaseClient) {
        const { error } = await supabaseClient.auth.signOut();
        if (error) {
          /* signOut error — non-blocking */
        }
      }
    } catch {
      /* signOut call error — non-blocking */
    }

    // 2. Supprimer le token legacy du localStorage et de l'en-tête axios
    setSession(null);

    // 3. Supprimer l'API key
    localStorage.removeItem('X_API_KEY');

    // 4. Nettoyer les données de shift actif (vue chauffeur)
    localStorage.removeItem('txapp_active_shift_data');

    // 5. Nettoyer TOUS les tokens Supabase restants du localStorage
    // (au cas où signOut n'aurait pas tout nettoyé)
    try {
      const keysToRemove = Object.keys(localStorage).filter(key =>
        key.includes('-auth-token') ||
        key.includes('supabase') ||
        key.includes('sb-')
      );
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
    } catch {
      /* cleanup error — non-blocking */
    }

    // 6. Supprimer le token authToken legacy
    localStorage.removeItem('authToken');

    dispatch({ type: "LOGOUT" });
  };

  const updateProfile = async (profileData) => {
    // Mettre à jour en base de données
    const updatedUser = await updateUserProfile(state.user.id, profileData);

    // Mettre à jour l'état local avec l'utilisateur mis à jour
    dispatch({
      type: "UPDATE_PROFILE",
      payload: {
        user: updatedUser,
      },
    });

    return { success: true };
  };

  const refreshUserProfile = async () => {
    if (state.user?.id) {
      const updatedProfile = await getUserProfile(state.user.id);
      dispatch({
        type: "UPDATE_PROFILE",
        payload: {
          user: updatedProfile,
        },
      });
      return updatedProfile;
    }
  };

  if (!children) {
    return null;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        logout,
        updateProfile,
        refreshUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};